import { useCallback, useMemo } from "react";
import { useMyProducts } from "./useMyProducts";
import { useFullCurrentUser } from "./useCurrentUser";
import { useStores } from "./useStores";
import type { MyProduct } from "../types/products";

export interface UseProductFeaturesResult {
  /** The active product's plan codes for the current user type (or a debug override). */
  productPlans: string[];
  /** The product currently active for this user, if resolvable. */
  activeProduct: MyProduct | undefined;
  matchesProductPlan: (plan: string) => boolean;
  matchesProductPlans: (plans: string[] | null | undefined) => boolean;
  proskillPlans: string[];
  msPlans: string[];
  wblPlans: string[];
  isLoading: boolean;
  isDebugOverride: boolean;
  setDebugProductPlan: (newPlan: string[] | null) => void;
  refetch: () => void;
}

/** Developer override stored in localStorage; when set it replaces productPlans. */
const getDebugPlans = (): string[] | null => {
  try {
    const stored = localStorage.getItem("debug_product_plan");
    return stored ? (JSON.parse(stored) as string[]) : null;
  } catch (e) {
    console.error("Invalid debug_product_plan in localStorage", e);
    return null;
  }
};

/**
 * Returns the product-plan codes for the user's ACTIVE product, scoped to the
 * active user type: climbers (`cli-`) get the product's `learnerPlanCodes`,
 * everyone else gets `educatorPlanCodes`. The active product is the one the
 * user selected post-login (persisted as `activeProductCode`), or the sole
 * product when they only have one.
 *
 * Example:
 *   const { matchesProductPlan } = useProductFeatures();
 *   if (matchesProductPlan("ms-staff")) { ... }
 */
export const useProductFeatures = (): UseProductFeaturesResult => {
  const { products, loading, refetch } = useMyProducts();
  const { activeUserRecord } = useFullCurrentUser();
  const { userStore } = useStores();

  const activeProductCode: string | null = userStore?.getActiveProductCode();

  const activeProduct = useMemo<MyProduct | undefined>(() => {
    if (activeProductCode) {
      const match = products.find((p) => p.code === activeProductCode);
      if (match) return match;
    }
    // Fall back to the sole product when there's exactly one.
    return products.length === 1 ? products[0] : undefined;
  }, [products, activeProductCode]);

  const isClimber = activeUserRecord?.role === "climber";

  const debugProductPlans = getDebugPlans();

  const productPlans = useMemo<string[]>(() => {
    if (debugProductPlans) return debugProductPlans;
    if (isClimber) {
      if (!activeProduct) return [];
      return activeProduct.learnerPlanCodes;
    }
    // Educators get the union of plan codes across all their products so that
    // features from every product they belong to are available simultaneously.
    return [...new Set(products.flatMap((p) => p.educatorPlanCodes))];
  }, [debugProductPlans, activeProduct, isClimber, products]);

  // console.info("Active product:", activeProduct?.code, productPlans);
  const proskillPlans = useMemo(
    () => productPlans.filter((plan) => plan.startsWith("proskills")),
    [productPlans],
  );
  const wblPlans = useMemo(
    () => productPlans.filter((plan) => plan.startsWith("wbl")),
    [productPlans],
  );
  const msPlans = useMemo(
    () => productPlans.filter((plan) => plan.startsWith("ms-")),
    [productPlans],
  );

  // For internal developer use only.
  const setDebugProductPlan = useCallback((newPlan: string[] | null) => {
    if (Array.isArray(newPlan)) {
      localStorage.setItem("debug_product_plan", JSON.stringify(newPlan));
    } else {
      localStorage.removeItem("debug_product_plan");
    }
  }, []);

  const matchesProductPlan = useCallback(
    (plan: string) => productPlans.includes(plan),
    [productPlans],
  );

  const matchesProductPlans = useCallback(
    (plans: string[] | null | undefined) => {
      if (!plans) return false;
      return productPlans.some((plan) => plans.includes(plan));
    },
    [productPlans],
  );

  return {
    productPlans,
    activeProduct,
    matchesProductPlan,
    matchesProductPlans,
    proskillPlans,
    msPlans, // TODO - remove this
    wblPlans, // TODO - remove this
    isLoading: loading,
    isDebugOverride: debugProductPlans !== null,
    setDebugProductPlan,
    refetch: () => {
      refetch();
    },
  };
};
